import { Avatar, Box, Divider, Typography, useTheme } from "@mui/material"
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined"
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted"
import { useTranslation } from "providers/i18n"
import { LocationIcon, StoreIcon } from "components/icons"
import { useMemo } from "react"
import { getIcon } from "utils/icon"
import { IMultiLanguageInfo, IOccupantDetail } from "utils/feature"

export interface IStandardShopPromotionHeaderProps
  extends Pick<IOccupantDetail, "category" | "level"> {
  occupantName: IMultiLanguageInfo | string
  logo?: string
  hours?: string
}

export const StandardShopPromotionHeader: React.FC<
  IStandardShopPromotionHeaderProps
> = ({ occupantName, logo = "", level = "", category = {}, hours = "" }) => {
  const { t } = useTranslation()

  const theme = useTheme()

  const {
    headerLogo,
    headerTitle,
    divider,
    featureLabelWrapper,
    featureLabel,
    icon,
    textContainer,
    labelText,
    contentText,
  } = theme.components?.[`ShopPromotionHeader`].styleOverrides || {}

  const locationIcon = useMemo(
    () => getIcon(theme?.assets?.locationIcon) || <LocationIcon />,
    [theme]
  )

  const clockIcon = useMemo(
    () =>
      getIcon(theme?.assets?.appClock?.clockIcon) || (
        <AccessTimeOutlinedIcon color="primary" />
      ),
    [theme]
  )

  const categoryIcon = useMemo(
    () => getIcon(theme?.assets?.categoryIcon) || <FormatListBulletedIcon />,
    [theme]
  )

  return (
    <>
      <Avatar sx={headerLogo} src={logo}>
        <StoreIcon />
      </Avatar>
      <Typography variant="h5" sx={headerTitle}>
        {t(occupantName)}
      </Typography>
      <Divider orientation="vertical" sx={divider} />

      <Box sx={featureLabelWrapper}>
        <Box sx={featureLabel}>
          <Box sx={icon}>{locationIcon}</Box>
          <Box sx={textContainer}>
            <Typography sx={labelText}>{t("Location")}</Typography>
            <Typography variant="subtitle2" sx={contentText}>
              {t(level)}
            </Typography>
          </Box>
        </Box>

        <Box sx={featureLabel}>
          <Box sx={icon}>{categoryIcon}</Box>
          <Box sx={textContainer}>
            <Typography sx={labelText}>{t("Category")}</Typography>
            <Typography variant="subtitle2" sx={contentText}>
              {t(category)}
            </Typography>
          </Box>
        </Box>

        <Box sx={featureLabel}>
          <Box sx={icon}>{clockIcon}</Box>
          <Box sx={textContainer}>
            <Typography sx={labelText}>{t("Operation Time")}</Typography>
            <Typography sx={contentText}>{hours || "-"}</Typography>
          </Box>
        </Box>
      </Box>
    </>
  )
}
